import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ACCORDION_OVERWRITES } from './_overwrites/accordion';
import { ChevronDown } from 'lucide-react';

/* ---------------- Root ---------------- */

const accordionRootVariants = cva(ACCORDION_OVERWRITES.classNames.root.base, {
  variants: {
    variant: ACCORDION_OVERWRITES.classNames.root.variants,
  },
  defaultVariants: {
    variant: 'filter',
  },
});

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & VariantProps<typeof accordionRootVariants>
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Root ref={ref} className={cn(accordionRootVariants({ variant, className }))} {...props} />
));
Accordion.displayName = 'Accordion';

/* ---------------- Item ---------------- */

const accordionItemVariants = cva(ACCORDION_OVERWRITES.classNames.item.base, {
  variants: {
    variant: ACCORDION_OVERWRITES.classNames.item.variants,
  },
  defaultVariants: {
    variant: 'filter',
  },
});

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & VariantProps<typeof accordionItemVariants>
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn(accordionItemVariants({ variant, className }))} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

/* ---------------- Content ---------------- */

const accordionContentVariants = cva(ACCORDION_OVERWRITES.classNames.content.base, {
  variants: {
    variant: ACCORDION_OVERWRITES.classNames.content.variants,
  },
  defaultVariants: {
    variant: 'filter',
  },
});

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & VariantProps<typeof accordionContentVariants>
>(({ className, variant, children, ...props }, ref) => (
  <AccordionPrimitive.Content ref={ref} className={cn(accordionContentVariants({ variant, className }))} {...props}>
    {children}
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
